/**
 * 미션 제출 페이지 — 다차수 미션 수평 탭 네비게이션
 *
 * 2차수 이상의 미션이 있을 때 차수별 탭을 표시한다.
 * 모바일: 차수 번호 + 상태 dot / 데스크탑: 차수 번호 + 제목 + 배지 텍스트
 */

import type { MissionRound } from '@/lib/seedingPublicApi'

// ==================== 다차수 미션 관련 타입 ====================

/** 차수별 상태 (마감 여부) */
export type RoundStatus = 'open' | 'expired'

/** 차수별 제출 상태 */
export type RoundSubmissionStatus = 'not_submitted' | 'submitted' | 'approved' | 'rejected'

/** 차수별 정보 (상태 + 제출 상태 + 제출 내역) */
export interface RoundInfo {
  round: MissionRound
  status: RoundStatus
  submissionStatus: RoundSubmissionStatus
}

interface RoundTabsProps {
  roundsInfo: RoundInfo[]
  selectedRound: number
  forceMobile: boolean
  onSelectRound: (round: number) => void
}

export function RoundTabs({
  roundsInfo,
  selectedRound,
  forceMobile,
  onSelectRound,
}: RoundTabsProps) {
  return (
    <div className={`flex pt-6 border-b border-border ${forceMobile ? '' : 'md:gap-6'}`}>
      {roundsInfo.map((info) => {
        const isSelected = selectedRound === info.round.round
        const deadline = new Date(info.round.deadline_at)
        const now = new Date()
        const diffMs = deadline.getTime() - now.getTime()
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
        const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        const isExpired = diffMs <= 0

        // 상태 배지 (데스크탑용)
        const badgeConfig: Record<RoundSubmissionStatus, { text: string; className: string } | null> = {
          approved: { text: '승인', className: 'bg-green-100 text-green-700' },
          submitted: { text: '검수대기', className: 'bg-primary/10 text-primary' },
          rejected: { text: '반려', className: 'bg-red-100 text-red-700' },
          not_submitted: isExpired ? { text: '미제출', className: 'bg-red-100 text-red-700' } : null,
        }
        const badge = badgeConfig[info.submissionStatus]

        // 모바일 dot 색상
        const dotColor = info.submissionStatus === 'submitted' ? 'bg-primary'
          : info.submissionStatus === 'rejected' || (info.submissionStatus === 'not_submitted' && isExpired) ? 'bg-red-500'
          : info.submissionStatus === 'not_submitted' ? 'bg-outline-variant'
          : info.submissionStatus === 'approved' ? 'bg-green-500'
          : null

        // 남은 시간 텍스트
        const remainingText = isExpired
          ? '마감됨'
          : diffDays > 0
            ? `D-${diffDays}`
            : `${diffHours}시간 남음`

        return (
          <button
            key={info.round.round}
            onClick={() => onSelectRound(info.round.round)}
            className={`
              flex-1 pb-3 text-center text-sm transition-all border-b-2 -mb-px
              ${forceMobile ? '' : 'md:flex-none md:text-left'}
              ${isSelected
                ? 'border-primary text-primary'
                : 'border-transparent text-on-surface-variant hover:text-on-surface hover:border-outline-variant'
              }
            `}
          >
            {/* 모바일: 차수 번호 + dot */}
            <div className={forceMobile ? '' : 'md:hidden'}>
              <span className="font-medium">{info.round.round}차</span>
              <div className="flex justify-center mt-1.5 h-3">
                {dotColor && <span className={`inline-block w-2.5 h-2.5 rounded-full ${dotColor}`} />}
              </div>
            </div>
            {/* 데스크탑: 차수 번호 + 제목 + 배지 텍스트 */}
            <div className={forceMobile ? 'hidden' : 'hidden md:block'}>
              <span className="font-medium">
                <span className="text-xs text-outline-variant mr-1">{info.round.round}</span>
                {info.round.title || `${info.round.round}차`}
              </span>
              <div className="text-xs mt-1">
                {badge
                  ? <span className={`px-1.5 py-0.5 rounded font-medium ${badge.className}`}>{badge.text}</span>
                  : info.submissionStatus !== 'approved' && info.submissionStatus !== 'submitted'
                    ? <span className={isExpired ? 'text-red-500' : 'text-outline-variant'}>{remainingText}</span>
                    : <span>&nbsp;</span>
                }
              </div>
            </div>
          </button>
        )
      })}
    </div>
  )
}
